import { Box, Tooltip, IconButton, Typography } from '@mui/material';
import { Add, Close } from '@mui/icons-material';

import { useForm, FormData } from '@carcareers/contexts';
import { AutocompleteSearch, SearchParams } from '@carcareers/components';

export interface ListInputProps<T> {
  name: string;
  label?: string;
  tooltipLabel?: string;
  removeLabel?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  search: (params: SearchParams) => Promise<T | any>;
}

const ListItem = <T extends FormData>({
  item,
  removeLabel,
  name,
}: { item: T } & Partial<ListInputProps<T>>) => {
  const { getData, setData,  } = useForm<T>();
  const handleRemove = (item: T) =>
    setData(
      name ?? '',
      getData<T[]>(name ?? '')?.filter((d: T) => d.id !== item.id),
    );

  const getItemLabel = () => {
    if (!item) return 'Unknown';
    
    if (item.user?.email) {
      return `${item.id} - ${item.user.email}`;
    }
    
    if (item.user?.firstName || item.user?.lastName) {
      const firstName = item.user.firstName || '';
      const lastName = item.user.lastName || '';
      return `${item.id} - ${firstName} ${lastName}`.trim();
    }
    
    if (item.name) {
      return `${item.id} - ${item.name}`;
    }
    
    return `ID: ${item.id}`;
  };
  
  const label = getItemLabel();

  return (
    <Tooltip arrow key={item.id} title={removeLabel} onClick={() => handleRemove(item)} placement="top">
      <Box
        sx={{
          py: 1,
          pl: 2,
          pr: 1,
          display: 'flex',
          borderRadius: 2,
          alignItems: 'center',
          transition: 'all 100ms',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'grey.100',
            py: 1.5,
            pl: 2.5,
          },
        }}
      >
        <Typography sx={{ flex: 1 }}>{label.length > 42 ? `${label.substring(0, 42)}...` : label}</Typography>
        <div>
          <IconButton size="small" aria-label="remove item">
            <Close fontSize="small" />
          </IconButton>
        </div>
      </Box>
    </Tooltip>
  );
};

export const ListInput = <T extends FormData>({
  name,
  label = 'Add',
  tooltipLabel = 'Add',
  removeLabel = 'Remove',
  search,
}: ListInputProps<T>) => {
  const { data, setData, getData } = useForm<T>();
  
  const searchPath = `${name}_search`;

  const handleAdd = () => {
    const selectedValue = getData(searchPath);
    if (!selectedValue) return;
    
    if (data?.[name]) {
      setData(name, [...data[name], selectedValue]);
    } else {
      setData(name, [selectedValue]);
    }
    
    setData(searchPath, null);
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
        <AutocompleteSearch
          name={searchPath}
          search={search}
          label={label}
        />
        <Tooltip title={tooltipLabel}>
          <div>
            <IconButton 
              disabled={!getData(searchPath)} 
              color="secondary" 
              onClick={handleAdd} 
              sx={{ width: 'fit-content' }}
            >
              <Add />
            </IconButton>
          </div>
        </Tooltip>
      </Box>
      {data?.[name]?.map((item: T) => (
        <ListItem key={item.id} item={item} name={name} removeLabel={removeLabel} />
      ))}
    </>
  );
};
